import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";



import { useSearchParams } from "react-router-dom";



import { cn } from "@/lib/utils";



import { Input } from "../input";


const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", className)}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-slate-200/50 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors odd:bg-white even:bg-[#F8FAFD] hover:bg-slate-200/50 data-[state=selected]:bg-slate-200",
      className
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      "h-12 whitespace-nowrap bg-[#E4EBF3] px-4 text-start align-middle font-medium text-slate-700 [&:has([role=checkbox])]:pe-0",
      className
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      "border border-[#E4EBF3] p-4 align-middle [&:has([role=checkbox])]:pe-0",
      className
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-slate-400", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

const TablePagination = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { totalItems: number }
>(({ totalItems }, ref) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const pageSizeOptions = [5, 10, 20, 50, 100];
  const defaultPageSize = pageSizeOptions[0];

  const requestedSize = Number(searchParams.get("size"));

  const pageSize = pageSizeOptions.includes(requestedSize)
    ? requestedSize
    : defaultPageSize;

  const currentPage = Number(searchParams.get("page")) || 1;

  const totalPages = Math.ceil(totalItems / pageSize);

  const hasData = totalItems > 0;

  const updatePage = (page: number) => {
    if (page < 1 || page > totalPages || totalPages === 0) return;

    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", page.toString());
      return params;
    });
  };

  const updatePageSize = (size: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", "1");
      params.set("size", size.toString());
      return params;
    });
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    // اگر داده‌ای وجود نداشته باشد، هیچ شماره صفحه‌ای نمایش نده
    if (!hasData) {
      return (
        <>
          <button
            disabled={true}
            className="cursor-not-allowed px-3 py-2 text-gray-400"
          >
            <ChevronLeft size={16} className="text-gray-400" />
          </button>
          <button
            disabled={true}
            className="cursor-not-allowed px-3 py-2 text-gray-400"
          >
            <ChevronRight size={16} className="text-gray-400" />
          </button>
        </>
      );
    }

    const start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);

    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => updatePage(i)}
          disabled={false} // همه دکمه‌های نمایش داده شده معتبر هستند
          className={`px-3 py-2 transition-colors ${
            i === currentPage
              ? "font-bold text-primary"
              : "text-gray-700 hover:bg-primary/20"
          }`}
        >
          {i}
        </button>
      );
    }

    return (
      <>
        <button
          onClick={() => updatePage(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-3 py-2 transition-colors ${
            currentPage === 1
              ? "cursor-not-allowed text-gray-400"
              : "text-gray-700 hover:bg-primary/20"
          }`}
        >
          <ChevronLeft
            size={16}
            className={currentPage === 1 ? "text-gray-400" : "text-primary"}
          />
        </button>

        {pages}

        <button
          onClick={() => updatePage(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className={`px-3 py-2 transition-colors ${
            currentPage >= totalPages
              ? "cursor-not-allowed text-gray-400"
              : "text-gray-700 hover:bg-primary/20"
          }`}
        >
          <ChevronRight
            size={16}
            className={
              currentPage >= totalPages ? "text-gray-400" : "text-primary"
            }
          />
        </button>
      </>
    );
  };

  return (
    <div
      ref={ref}
      className="flex h-16 items-center border border-slate-300 bg-white px-10"
      dir="ltr"
    >
      <select
        className="me-5 rounded border border-gray-300 px-3 py-1"
        value={pageSize}
        onChange={(e) => updatePageSize(Number(e.target.value))}
      >
        {pageSizeOptions.map((size) => (
          <option key={size} value={size}>
            نمایش {size} نتیجه
          </option>
        ))}
      </select>
      <div className="flex divide-x divide-slate-300 border border-slate-300">
        {renderPageNumbers()}
      </div>
    </div>
  );
});

TablePagination.displayName = "TablePagination";

const TableSearch = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    debounceMs?: number;
    placeholder?: string;
    searchKey?: string;
  }
>(
  (
    {
      className,
      debounceMs = 1500,
      placeholder = "جستجو",
      searchKey = "search",
      ...props
    },
    ref
  ) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const searchValue = searchParams.get(searchKey) ?? "";

    const [inputValue, setInputValue] = React.useState(searchValue);

    const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout>>();

    React.useEffect(() => {
      setInputValue(searchValue);
    }, [searchValue]);

    const updateSearchParams = React.useCallback(
      (value: string) => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          setSearchParams((prev) => {
            const params = new URLSearchParams(prev);

            params.delete("page");
            params.delete("size");

            if (value.trim()) {
              params.set(searchKey, value.trim());
            } else {
              params.delete(searchKey);
            }

            return params;
          });
        }, debounceMs);
      },
      [setSearchParams, searchKey, debounceMs]
    );

    React.useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      setInputValue(value);

      updateSearchParams(value);
    };

    return (
      <div ref={ref} className={className} {...props}>
        <Input
          placeholder={placeholder}
          value={inputValue}
          onChange={handleSearch}
          className="h-full"
        />
      </div>
    );
  }
);

export default TableSearch;

TableSearch.displayName = "TableSearch";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TablePagination,
  TableSearch
};